import { useState, useRef, useMemo } from 'react'
import { motion } from 'framer-motion'
import { EntityCard, FloatingActionButton, GlassPanel, Icon, LimitUpgradePrompt } from '../components/common'
import { EntityModal } from '../components/EntityModal'
import { useCharacters, useCharacterOperations } from '../hooks/useFirestore'
import { useProject } from '../context/ProjectContext'
import { useLimits } from '../hooks/useLimits'
import { deleteFiles, extractPublicIdFromUrl } from '../services/storage'
import { updateBidirectionalLinks, removeBidirectionalLinksOnDelete } from '../services/bidirectionalLinks'
import { COLLECTIONS } from '../services/firestore'
import './CharactersView.css'

// LocalStorage key for tier filter persistence
const TIER_FILTER_KEY = 'tierFilters_characters'

/**
 * Helper to get image URL from either string or object format
 * Supports both legacy (string) and new ({ url, publicId }) formats
 */
const getImageUrl = (image) => {
  if (!image) return ''
  if (typeof image === 'string') return image
  return image.url || ''
}

/**
 * Helper to collect all image publicIds from an entity
 * Returns array of publicIds for deletion
 */
const collectImagePublicIds = (entity) => {
  const publicIds = []
  
  // Primary image
  if (entity.primaryImage) {
    if (typeof entity.primaryImage === 'object' && entity.primaryImage.publicId) {
      publicIds.push(entity.primaryImage.publicId)
    } else if (typeof entity.primaryImage === 'string') {
      // Try to extract from URL for legacy data
      const extracted = extractPublicIdFromUrl(entity.primaryImage)
      if (extracted) publicIds.push(extracted)
    }
  }
  
  // Extra images
  if (entity.extraImages && Array.isArray(entity.extraImages)) {
    for (const img of entity.extraImages) {
      if (typeof img === 'object' && img.publicId) {
        publicIds.push(img.publicId)
      } else if (typeof img === 'string') {
        const extracted = extractPublicIdFromUrl(img)
        if (extracted) publicIds.push(extracted)
      } else if (typeof img === 'object' && img.url) {
        const extracted = extractPublicIdFromUrl(img.url)
        if (extracted) publicIds.push(extracted)
      }
    }
  }
  
  return publicIds.filter(Boolean)
}

// Page transition variants
const pageVariants = {
  initial: { 
    opacity: 0, 
    y: 20,
    filter: 'blur(4px)'
  },
  animate: { 
    opacity: 1, 
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: { 
    opacity: 0, 
    y: -20,
    filter: 'blur(4px)',
    transition: {
      duration: 0.2
    }
  }
}

// Container variants for staggered children
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
}

/**
 * CharactersView - Grid display of character cards with CRUD operations
 */
export default function CharactersView() {
  // Modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState('create')
  const [selectedCharacter, setSelectedCharacter] = useState(null)
  const [originRect, setOriginRect] = useState(null)
  const [limitPromptOpen, setLimitPromptOpen] = useState(false)
  const fabRef = useRef(null)

  // Tier filter state (default: A and B shown, C hidden)
  const [tierFilters, setTierFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(TIER_FILTER_KEY)
      return saved ? JSON.parse(saved) : { A: true, B: true, C: false }
    } catch {
      return { A: true, B: true, C: false }
    }
  })

  // Project context for scoped data access and permissions
  const { projectId, can } = useProject()
  const canCreate = can('create')
  
  // Limits checking
  const { canCreate: canCreateEntity, getLimit, hasLimits } = useLimits(projectId)

  // Firestore data and operations (project-scoped)
  const { data: characters, loading, error } = useCharacters(projectId, {
    orderByField: 'createdAt',
    orderDirection: 'desc',
  })
  const characterOps = useCharacterOperations(projectId)

  // Filter characters by tier
  const filteredCharacters = useMemo(() => {
    if (!characters) return []
    return characters.filter(character => {
      const tier = (character.tier || 'B').toUpperCase()
      return tierFilters[tier] !== false
    })
  }, [characters, tierFilters])

  // Toggle a tier filter
  const toggleTier = (tier) => {
    const newFilters = { ...tierFilters, [tier]: !tierFilters[tier] }
    setTierFilters(newFilters)
    try {
      localStorage.setItem(TIER_FILTER_KEY, JSON.stringify(newFilters))
    } catch {
      // Ignore localStorage errors
    }
  }

  // Handle create click - check limits first
  const handleCreateClick = (event) => {
    if (!canCreateEntity('characters')) {
      setLimitPromptOpen(true)
      return
    }
    openCreateModal(event)
  }

  // Open modal in create mode
  const openCreateModal = (event) => {
    setModalMode('create')
    setSelectedCharacter(null)
    if (event?.currentTarget) {
      setOriginRect(event.currentTarget.getBoundingClientRect())
    } else if (fabRef.current) {
      setOriginRect(fabRef.current.getBoundingClientRect())
    }
    setModalOpen(true)
  }

  // Open modal in edit mode
  const openEditModal = (character, event) => {
    setModalMode('edit')
    setSelectedCharacter(character)
    if (event?.currentTarget) {
      setOriginRect(event.currentTarget.getBoundingClientRect())
    }
    setModalOpen(true)
  }

  // Handle save (create or update)
  const handleSave = async (data) => {
    try {
      if (modalMode === 'create') {
        const newId = await characterOps.add(data)
        // Update bidirectional links for the new entity
        await updateBidirectionalLinks(projectId, COLLECTIONS.CHARACTERS, newId, null, data)
      } else {
        await characterOps.update(selectedCharacter.id, data)
        // Update bidirectional links (compare old vs new links)
        await updateBidirectionalLinks(projectId, COLLECTIONS.CHARACTERS, selectedCharacter.id, selectedCharacter, data)
      }
      setModalOpen(false)
    } catch (err) {
      console.error('Failed to save character:', err)
      throw err
    }
  }

  // Handle delete (also deletes associated Cloudinary images and cleans up bidirectional links)
  const handleDelete = async () => {
    try {
      // Collect all image publicIds from the character
      const publicIds = collectImagePublicIds(selectedCharacter)
      
      // Delete images from Cloudinary (don't fail if this fails)
      if (publicIds.length > 0) {
        console.log('Deleting Cloudinary images:', publicIds)
        const deleteResult = await deleteFiles(publicIds)
        if (deleteResult.errors?.length > 0) {
          console.warn('Some images failed to delete:', deleteResult.errors)
        }
      }
      
      // Clean up bidirectional links before deleting
      await removeBidirectionalLinksOnDelete(projectId, COLLECTIONS.CHARACTERS, selectedCharacter.id, selectedCharacter)
      
      // Delete the character document from Firestore
      await characterOps.remove(selectedCharacter.id)
      setModalOpen(false)
    } catch (err) {
      console.error('Failed to delete character:', err)
      throw err
    }
  }

  // Build card details from character data
  const getCardDetails = (character) => {
    const details = []
    
    if (character.attire) {
      details.push({ 
        label: 'Attire', 
        value: character.attire.slice(0, 20) + (character.attire.length > 20 ? '...' : '')
      })
    }
    
    if (character.hairStyle) {
      details.push({ 
        label: 'Hair', 
        value: character.hairStyle.slice(0, 20) + (character.hairStyle.length > 20 ? '...' : '')
      })
    }
    
    if (character.distinctFeatures) {
      details.push({ 
        label: 'Features', 
        value: character.distinctFeatures.slice(0, 20) + (character.distinctFeatures.length > 20 ? '...' : '')
      })
    }
    
    if (character.linkedLocations?.length > 0) {
      details.push({ 
        label: 'Locations', 
        value: character.linkedLocations.length.toString()
      })
    }
    
    return details.slice(0, 4)
  }

  // Get subtitle from description
  const getSubtitle = (character) => {
    if (!character.description) return 'No description'
    return character.description.slice(0, 50) + (character.description.length > 50 ? '...' : '')
  }

  return (
    <motion.div 
      className="characters-view"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Page Header */}
      <header className="characters-view__header">
        <div className="characters-view__title-group">
          <span className="characters-view__icon"><Icon name="characters" size={28} /></span>
          <h1 className="characters-view__title">Characters</h1>
        </div>
        <p className="characters-view__subtitle">
          {loading ? 'Loading...' : `${filteredCharacters.length} character${filteredCharacters.length !== 1 ? 's' : ''}`}
          {!loading && characters.length !== filteredCharacters.length && ` (${characters.length} total)`}
        </p>
      </header>

      {/* Tier Filter Bar */}
      {!loading && !error && characters.length > 0 && (
        <div className="characters-view__filters">
          <div className="characters-view__tier-filters">
            <span className="characters-view__filter-label">Tier:</span>
            {['A', 'B', 'C'].map(tier => (
              <button
                key={tier}
                className={`characters-view__tier-btn characters-view__tier-btn--${tier.toLowerCase()} ${tierFilters[tier] ? 'characters-view__tier-btn--active' : ''}`}
                onClick={() => toggleTier(tier)}
                title={`${tierFilters[tier] ? 'Hide' : 'Show'} Tier ${tier} characters`}
              >
                {tier}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <GlassPanel glow="amber" padding="lg" className="characters-view__error">
          <h3>Error loading characters</h3>
          <p>{error.message}</p>
        </GlassPanel>
      )}

      {/* Loading State */}
      {loading && !error && (
        <div className="characters-view__loading">
          <div className="characters-view__spinner" />
          <p>Loading characters...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && characters.length === 0 && (
        <GlassPanel glow="cyan" padding="xl" className="characters-view__empty">
          <div className="characters-view__empty-icon"><Icon name="characters" size={48} /></div>
          <h2>No characters yet</h2>
          <p>{canCreate ? 'Create your first character to start building your story.' : 'No characters have been added to this project yet.'}</p>
        </GlassPanel>
      )}

      {/* No Results State (when filters hide all) */}
      {!loading && !error && characters.length > 0 && filteredCharacters.length === 0 && (
        <div className="characters-view__no-results">
          <div className="characters-view__no-results-icon"><Icon name="search" size={48} /></div>
          <h3>No matching characters</h3>
          <p>Try adjusting your tier filters to see more characters.</p>
        </div>
      )}

      {/* Character Grid */}
      {!loading && !error && filteredCharacters.length > 0 && (
        <motion.div 
          className="characters-view__grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {filteredCharacters.map((character, index) => (
            <EntityCard
              key={character.id}
              type="character"
              name={character.name}
              image={getImageUrl(character.primaryImage)}
              subtitle={getSubtitle(character)}
              details={getCardDetails(character)}
              tier={character.tier}
              index={index}
              onClick={(e) => openEditModal(character, e)}
            />
          ))}
        </motion.div>
      )}

      {/* Floating Action Button - only shown if user can create */}
      {canCreate && (
        <div ref={fabRef} data-onboarding="add-entity">
          <FloatingActionButton
            icon={<Icon name="plus" size={24} />}
            variant="primary"
            size="lg"
            position="bottom-right"
            label="Add new character"
            onClick={handleCreateClick}
          />
        </div>
      )}

      {/* Entity Modal */}
      <EntityModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        entityType="character"
        mode={modalMode}
        initialData={selectedCharacter}
        onSave={handleSave}
        onDelete={modalMode === 'edit' ? handleDelete : undefined}
        originRect={originRect}
      />

      {/* Limit Upgrade Prompt */}
      <LimitUpgradePrompt
        isOpen={limitPromptOpen}
        onClose={() => setLimitPromptOpen(false)}
        entityType="characters"
        limit={getLimit('characters')}
      />
    </motion.div>
  )
}

